import { useParams, useSearchParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import CohortsForm from 'components/forms/Cohorts/Cohorts.tsx';
import useCampaign from 'hooks/useCampaign.ts';
import useReferenceData from 'hooks/useReferenceData.ts';
import { IterationCohort } from 'schemas/IterationCohort.schema.ts';
import Back from 'components/Back/index.tsx';
import IterationPageHeader from 'components/IterationPageHeader/index.tsx';
import ApiErrorDetails from 'components/ApiErrorDetails/index.tsx';
import useIterationLock from 'hooks/useIterationLock.ts';
import useProtectRoute from 'hooks/useProtectRoute.ts';
import ProgressIndicator from 'components/ProgressIndicator/index.tsx';
import { IIterationLevelParams } from 'utils/parameterUtils';
import useUpdateIteration from 'queries/mutate/updateIteration.ts';
import { IterationDetailsFormValues } from 'schemas/Iteration.schema.ts';
import { getIterationById } from 'utils/iterationUtils.ts';
import { ApiError } from 'schemas/ApiError.schema';
import useCampaignNavigate from 'hooks/useCampaignNavigate';
import { getBackToUrl } from 'utils/cohortUtils';
import useFetchTemplateForCampaign from 'queries/fetch/fetchTemplateForCampaign.ts';

export default function CohortsPage() {
  const { teamId, campaignId, iterationId } = useParams() as IIterationLevelParams;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { campaign } = useCampaign();
  const [searchParams] = useSearchParams();
  const { referenceData } = useReferenceData();
  const initialRenderRef = useRef(true);
  useProtectRoute();
  const [apiError, setApiError] = useState<ApiError | null>(null);
  const updateIteration = useUpdateIteration();
  const { navigateToCampaign } = useCampaignNavigate();
  const { lockIterationIfRequired, error: lockError } = useIterationLock({ teamId, campaignId, iterationId });
  const { data: campaignTemplate } = useFetchTemplateForCampaign({
    teamId,
    campaignId,
    isEnabled: Boolean(campaign.CampaignConfig?.Template),
  });

  const { cohorts } = referenceData;

  const existingIterations = campaign.CampaignConfig?.Iterations || [];
  const existingIteration = existingIterations.find((iteration) => iteration.ID === iterationId);
  const existingCohorts = existingIterations.find((iteration) => iteration.ID === iterationId)
    ?.IterationCohorts || [];

  const onSuccessfulSubmission = async (newCohorts: IterationCohort[]) => {
    setApiError(null);

    const currentIteration = getIterationById(campaign, iterationId);
    const updatedIteration = {
      ...currentIteration,
      IterationCohorts: newCohorts,
    };

    try {
      setIsSubmitting(true);
      await updateIteration.mutateAsync({
        campaignId,
        iteration: updatedIteration as IterationDetailsFormValues,
        iterationId,
        teamId,
      });

      const redirectPage = searchParams.get('redirectPage');
      if (redirectPage) {
        navigateToCampaign(redirectPage);
        return;
      }

      if (searchParams.get('mode') === 'new') {
        navigateToCampaign(`iteration/${iterationId}/message-plan?mode=new`);
        return;
      }

      navigateToCampaign(`iteration/${iterationId}/summary`);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'An error occurred saving the cohorts';
      setApiError({ message });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (initialRenderRef.current) {
      initialRenderRef.current = false;
      lockIterationIfRequired();
    }
  }, [lockIterationIfRequired]);

  if (!existingIteration) {
    return <ProgressIndicator text="Loading iteration details..." />;
  }

  if (campaignTemplate?.['IterationDetails.IterationCohorts.input.display'] === 'hidden') {
    if (searchParams.get('mode') === 'new') {
      navigateToCampaign(`iteration/${iterationId}/message-plan?mode=new`);
      return null;
    }

    navigateToCampaign(`iteration/${iterationId}/summary`);
    return null;
  }

  return (
    <>
      <div className="nhsuk-u-margin-bottom-6">
        <Back
          backTo={getBackToUrl(searchParams, teamId, campaignId, iterationId)}
          label="Iteration summary"
        />
      </div>
      {existingIteration && <IterationPageHeader iteration={existingIteration} title="Choose cohorts for iteration" />}

      <CohortsForm
        onSuccessfulSubmission={onSuccessfulSubmission}
        existingCohorts={existingCohorts
          .toSorted((a, b) => (a.Priority ?? 0) - (b.Priority ?? 0))
          .map(
            (existingCohort) => existingCohort.CohortLabel as string,
          )}
        campaignTarget={campaign.CampaignConfig?.Target || ''}
        cohorts={cohorts}
        existingRules={existingIteration.IterationRules || []}
        isSubmitting={isSubmitting}
      />
      <ApiErrorDetails apiError={apiError || lockError} />
    </>
  );
}
